<template>
  <div v-if="visible" :class="['notification-bar', severityClass]">
    <span class="icon" :class="computedIconClass"></span>
    <div class="text-content">
      <slot>
        <strong>{{ mainText }}</strong>
        <span v-if="subText" class="sub-text">{{ subText }}</span>
      </slot>
    </div>
    <button v-if="showCloseButton" class="close-button" @click="closeNotification">✕</button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface Props {
  severity?: 'info' | 'warn' | 'error' | 'success' | 'ai';
  mainText: string;
  subText?: string;
  icon?: string;
  showCloseButton?: boolean;
  invertStyle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  severity: 'info',
  subText: '',
  icon: '',
  showCloseButton: true,
  invertStyle: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const visible = ref(true);

watch(
  () => [props.mainText, props.subText],
  () => {
    visible.value = true;
  },
);

const closeNotification = () => {
  visible.value = false;
  emit('close');
};

const severityClass = computed(() => {
  if (props.severity === 'ai' && props.invertStyle) {
    return 'notification-ai-inverted';
  }

  switch (props.severity) {
    case 'info':
      return 'notification-info';
    case 'warn':
      return 'notification-warn';
    case 'error':
      return 'notification-error';
    case 'success':
      return 'notification-success';
    case 'ai':
      return 'notification-ai';
    default:
      return 'notification-primary';
  }
});

const computedIconClass = computed(() => {
  if (props.icon) return props.icon;
  switch (props.severity) {
    case 'info':
      return 'pi pi-info-circle';
    case 'warn':
      return 'pi pi-exclamation-triangle';
    case 'error':
      return 'pi pi-times-circle';
    case 'success':
      return 'pi pi-check-circle';
    case 'ai':
      return 'pi pi-sparkles';
    default:
      return 'pi pi-info-circle';
  }
});
</script>

<style scoped lang="css">
@config "../../../tailwind.config.js";
@reference "tailwindcss";
@reference "tailwindcss/utilities";
.notification-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid transparent;
  border-radius: 4px;
  color: white;
  @apply my-4 p-4;
}

.notification-info {
  background-color: theme('colors.interactionblue.500');
  border-color: theme('colors.interactionblue.500');
}

.notification-warn {
  background-color: theme('colors.signalorange.500');
  border-color: theme('colors.signalorange.500');
}

.notification-error {
  background-color: theme('colors.signalred.500');
  border-color: theme('colors.signalred.500');
}

.notification-success {
  background-color: theme('colors.interactiongreen.500');
  border-color: theme('colors.interactiongreen.500');
}

.notification-ai {
  background-color: theme('colors.purple.500');
  border-color: theme('colors.purple.500');
}

.notification-ai-inverted {
  color: theme('colors.purple.500');
  border: 1px solid theme('colors.purple.500');
  background-color: theme('colors.white');
}

.notification-primary {
  color: var(--p-splitter-color);
  border: 1px solid var(--p-splitter-color);
  background-color: var(--p-toolbar-background);
}

.icon {
  font-size: 1.5rem;
  margin-right: 1rem;
}

.text-content {
  flex: 1;
  display: flex;
  gap: 1rem;
  align-items: center;
}

.sub-text {
  font-weight: normal;
}

.close-button {
  background: none;
  border: none;
  color: inherit;
  font-size: 0.75rem;
  cursor: pointer;
}
</style>
